import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { format } from "date-fns";
import { Flame, MessageSquare, PenSquare, ArrowLeft, FileText, Camera } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import BadgeChip, { useBadges } from "../components/BadgeChip";
import OpinionCard from "../components/OpinionCard";
import Reveal from "../components/Reveal";

export default function ProfilePage() {
  const { id } = useParams();
  const { user, setUser } = useAuth();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInput = useRef(null);
  const badges = useBadges();

  useEffect(() => {
    setData(null);
    setNotFound(false);
    api.get(`/members/${id}/profile`).then((r) => setData(r.data)).catch(() => setNotFound(true));
  }, [id]);

  const uploadAvatar = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const up = await api.post("/uploads", fd);
      const r = await api.put("/users/me/avatar", { avatar_url: up.data.url });
      setUser(r.data);
      setData((d) => ({ ...d, member: { ...d.member, avatar_url: r.data.avatar_url } }));
      toast.success("Profile photo updated.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };

  if (notFound) {
    return (
      <div className="max-w-[1440px] mx-auto px-5 lg:px-10 py-32 text-center">
        <p className="font-display font-extrabold text-4xl uppercase">Member not found</p>
        <Link to="/voices" className="text-kinetic font-mono text-xs uppercase tracking-[0.2em] mt-6 inline-block">← Back to Voices</Link>
      </div>
    );
  }

  if (!data) {
    return <div className="min-h-[60vh] flex items-center justify-center"><p className="font-mono text-ash text-xs uppercase tracking-[0.3em] animate-pulse">Loading member…</p></div>;
  }

  const { member, opinions, comments, opinion_comments, stats } = data;
  const ownProfile = user && user.id === member.id;
  const saves = member.saves || [];
  const savedPhotos = saves.filter((s) => s.type === "photo");
  const savedStories = saves.filter((s) => s.type === "article");
  const activity = [
    ...comments.map((c) => ({ ...c, kind: "story", target: c.article_title, href: `/article/${c.article_slug}` })),
    ...opinion_comments.map((c) => ({ ...c, kind: "voice", target: c.opinion_title, href: "/voices" })),
  ].sort((a, b) => b.created_at.localeCompare(a.created_at)).slice(0, 10);

  const statCards = [
    { label: "Voices", value: stats.opinions, icon: PenSquare, color: "text-crimson" },
    { label: "Votes received", value: stats.upvotes_received, icon: Flame, color: "text-kinetic" },
    { label: "Comments", value: stats.comments, icon: MessageSquare, color: "text-cobalt" },
  ];

  return (
    <div data-testid="member-profile-page" className="max-w-[1440px] mx-auto px-5 lg:px-10 py-16 lg:py-24">
      <Reveal>
        <Link to="/voices" className="inline-flex items-center gap-2 text-ash hover:text-kinetic text-xs font-mono uppercase tracking-[0.2em] mb-10 transition-colors">
          <ArrowLeft size={13} /> Voices
        </Link>
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-8 mb-14">
          <div className="flex items-center gap-6">
            <div className="relative shrink-0 group">
              {member.avatar_url ? (
                <img data-testid="member-avatar" src={member.avatar_url} alt={member.name}
                  className="w-20 h-20 object-cover border border-white/10" />
              ) : (
                <span data-testid="member-avatar" className="w-20 h-20 bg-elevate border border-white/10 flex items-center justify-center font-display font-black text-3xl text-kinetic">
                  {member.name.slice(0, 2).toUpperCase()}
                </span>
              )}
              {ownProfile && (
                <>
                  <input ref={fileInput} data-testid="profile-avatar-file-input" type="file" accept="image/jpeg,image/png,image/gif,image/webp"
                    className="hidden" onChange={uploadAvatar} />
                  <button data-testid="profile-avatar-change-button" onClick={() => fileInput.current?.click()} disabled={uploading}
                    title="Change profile photo"
                    className="absolute inset-0 bg-obsidian/70 opacity-0 group-hover:opacity-100 flex flex-col items-center justify-center gap-1 transition-opacity duration-300 disabled:opacity-100">
                    <Camera size={16} className="text-kinetic" />
                    <span className="text-[9px] font-mono uppercase tracking-[0.15em] text-porcelain">{uploading ? "Uploading…" : "Change"}</span>
                  </button>
                </>
              )}
            </div>
            <div>
              <p className="text-xs font-mono uppercase tracking-[0.3em] text-kinetic mb-2">
                {member.role === "admin" ? "Editorial Desk" : "Member"} · since {format(new Date(member.created_at), "MMM yyyy")}
              </p>
              <h1 data-testid="member-name" className="font-display font-black uppercase text-3xl sm:text-5xl tracking-tight break-words">{member.name}</h1>
              {(badges[member.id] || []).length > 0 && (
                <div data-testid="member-badges" className="flex flex-wrap gap-2 mt-3">
                  {(badges[member.id] || []).map((b) => <BadgeChip key={b.id} badge={b} testid={`member-badge-${b.id}`} />)}
                </div>
              )}
            </div>
          </div>
          <div className="flex flex-wrap gap-4">
            {statCards.map((s) => (
              <div key={s.label} data-testid="member-stat-card" className="border border-white/10 bg-surface px-5 py-4 min-w-[110px]">
                <s.icon size={16} className={`${s.color} mb-2`} />
                <p className="font-display font-black text-2xl">{s.value}</p>
                <p className="text-[10px] font-mono uppercase tracking-[0.15em] text-ash mt-1">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </Reveal>

      {saves.length > 0 && (
        <Reveal>
          <section data-testid="member-scrapbook" className="mt-14">
            <h2 className="font-display font-extrabold uppercase text-2xl sm:text-3xl tracking-tight mb-8">
              The <span className="font-serif italic normal-case text-ash">Scrapbook</span>
            </h2>
            {savedPhotos.length > 0 && (
              <div className="grid grid-cols-3 lg:grid-cols-6 gap-3 mb-8">
                {savedPhotos.map((s, i) => (
                  <a key={s.key} href={s.image_url} target="_blank" rel="noopener noreferrer" data-testid={`scrapbook-photo-${i}`}
                    className="group relative block aspect-square overflow-hidden border border-white/10 hover:border-kinetic/50 transition-colors duration-300">
                    <img src={s.image_url} alt={s.title || "Saved photo"} loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  </a>
                ))}
              </div>
            )}
            {savedStories.length > 0 && (
              <div>
                {savedStories.map((s, i) => (
                  <Link key={s.key} to={s.url || "#"} data-testid={`scrapbook-story-${i}`}
                    className="group flex items-center justify-between gap-4 py-4 border-b border-white/5 hover:border-kinetic/40 transition-colors">
                    <span className="text-sm font-medium text-porcelain group-hover:text-kinetic transition-colors truncate">{s.title}</span>
                    <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-ash group-hover:text-kinetic shrink-0 transition-colors">Read →</span>
                  </Link>
                ))}
              </div>
            )}
          </section>
        </Reveal>
      )}

      <Reveal>
        <h2 className="font-display font-extrabold uppercase text-2xl sm:text-3xl tracking-tight mb-8">
          Published voices <span className="text-ash font-mono text-base align-middle">({opinions.length})</span>
        </h2>
      </Reveal>
      {opinions.length === 0 ? (
        <p className="text-ash font-mono text-xs uppercase tracking-[0.2em] py-10 border border-dashed border-white/15 text-center mb-16">
          No takes published yet.
        </p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mb-16">
          {opinions.map((o, i) => (
            <OpinionCard key={o.id} opinion={o} index={i} onOpen={() => { window.location.href = "/voices"; }} />
          ))}
        </div>
      )}

      <Reveal>
        <h2 className="font-display font-extrabold uppercase text-2xl sm:text-3xl tracking-tight mb-8">
          Recent activity
        </h2>
      </Reveal>
      {activity.length === 0 ? (
        <p className="text-ash font-mono text-xs uppercase tracking-[0.2em] py-10 border border-dashed border-white/15 text-center">
          Quiet so far — no comments yet.
        </p>
      ) : (
        <div className="space-y-3">
          {activity.map((c) => (
            <div key={c.id} data-testid="member-activity-item" className="border border-white/10 bg-surface p-5 flex items-start gap-4">
              <FileText size={15} className="text-ash mt-0.5 shrink-0" />
              <div className="min-w-0">
                <p className="text-sm text-porcelain/85 leading-relaxed">{c.body}</p>
                <p className="text-[10px] font-mono text-ash uppercase tracking-[0.15em] mt-2">
                  on {c.kind === "story" ? "story" : "voice"}{" "}
                  <Link to={c.href} className="text-kinetic hover:underline">{c.target}</Link>
                  {" · "}{format(new Date(c.created_at), "dd MMM yyyy")}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
